import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  saveClinicalDischargeSummary,
  getClinicalDischargeSummary,
} from "../../../api/apiService";
import { getPatientById } from "../../../Services/patientService";
import { sanitizeClinicalNote, sanitizeText } from "../../../utils/inputValidation";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";
import MedicineAutocomplete from "../../../components/common/MedicineAutocomplete";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";
import { printDocumentHtml, buildDischargeSummaryHtml, resolveHospitalName } from "../../../utils/printEngine";
import "../../../assets/scss/discharge-summary.scss";

const DischargeSummaryForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError, showWarning } =
    useToast();
  const { can, user } = useAuth();
  const canWriteDischarge = can(PERMISSIONS.CLINICAL_DISCHARGE_WRITE);
  const [patientData, setPatientData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const createEmptyMed = (id) => ({
    id,
    medicine: "",
    form: "Tablet",
    dosage: "",
    frequency: "",
    duration: "",
    durationUnit: "days",
    instructions: "",
    snomedCode: "",
    snomedDisplay: "",
  });
  const [dischargeMeds, setDischargeMeds] = useState(() =>
    Array.from({ length: 5 }, (_, i) => createEmptyMed(i + 1)),
  );
  const [nextMedId, setNextMedId] = useState(6);

  const updateDischargeMed = (id, field, value) => {
    setDischargeMeds((prev) =>
      prev.map((med) => (med.id === id ? { ...med, [field]: value } : med)),
    );
  };

  useEffect(() => {
    let isMounted = true;
    const loadPatient = async () => {
      let data = location.state?.patientData;
      const queryParams = new URLSearchParams(location.search);
      const patientIdFromQuery =
        queryParams.get("patientId") || location.state?.patientId;
      const targetId = data?._id || data?.id || patientIdFromQuery;

      if (targetId && (!data || !data.visits || data.visits.length === 0)) {
        try {
          const fetched = await getPatientById(targetId);
          if (fetched && isMounted) {
            data = {
              ...(data || {}),
              ...fetched,
              visitId:
                fetched.visitId ||
                fetched.visits?.[0]?.visitId ||
                data?.visitId,
            };
          }
        } catch (e) {
          console.warn("Could not load patient for discharge summary:", e);
        }
      }

      if (!isMounted) return;

      if (data) {
        setPatientData(data);
      } else {
        showWarning("Please select a patient first");
        setTimeout(() => navigate("/discharge-summary"), 1500);
      }
    };

    loadPatient();
    return () => {
      isMounted = false;
    };
  }, [location.state, location.search, navigate, showWarning]);

  // Load existing discharge summary and prefill form when patientData is set (form is mounted)
  useEffect(() => {
    if (!patientData) return;
    const visitId =
      patientData?.visitId || patientData?.visits?.[0]?.visitId || "";
    const patientId = patientData?._id || patientData?.id || "";
    if (!visitId) return;
    let cancelled = false;
    getClinicalDischargeSummary(visitId, patientId || undefined)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data ?? res ?? {};
        const formEl = document.querySelector("#DischargeSummaryForm");
        if (!formEl) return;
        // API returns camelCase keys; map to data-field attributes
        const fieldMap = {
          diagnosis: "diagnosis",
          admissionNotes: "admission",
          investigationsResults: "investigations",
          surgicalProcedures: "surgical_procedures",
          surgicalNote: "surgical_notes",
          conditionAtDischarge: "condition_at_discharge",
          clinicalSummary: "clinical_course",
          treatmentGiven: "treatment",
          followUpInstructions: "discharge",
          doctorSignature: "doctor_signature",
        };
        Object.entries(fieldMap).forEach(([apiKey, dataField]) => {
          const value = payload[apiKey];
          if (value == null || value === "") return;
          const el = formEl.querySelector(`[data-field="${dataField}"]`);
          if (el) {
            if (el.tagName === "SELECT") {
              const opt = Array.from(el.options).find(
                (o) => o.value === value || o.text === value,
              );
              if (opt) el.value = opt.value;
            } else {
              el.value = value;
            }
          }
        });
        // Prefill discharge medications into React state
        const meds = payload.dischargeMedications;
        if (Array.isArray(meds) && meds.length > 0) {
          const mapped = meds.map((m, i) => ({
            id: i + 1,
            medicine: m.medicine ?? m.drugName ?? m.drug_name ?? "",
            form: m.form ?? "Tablet",
            dosage: m.dosage ?? "",
            frequency: m.frequency ?? "",
            duration: m.duration ?? "",
            durationUnit: m.duration_unit ?? m.durationUnit ?? "days",
            instructions: m.instructions ?? "",
            snomedCode: m.snomed_code ?? m.snomedCode ?? "",
            snomedDisplay:
              m.snomed_display ??
              m.snomedDisplay ??
              m.medicine ??
              m.drugName ??
              "",
          }));
          // Pad to at least 5 rows
          while (mapped.length < 5) {
            mapped.push(createEmptyMed(mapped.length + 1));
          }
          setDischargeMeds(mapped);
          setNextMedId(mapped.length + 1);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error("Error fetching discharge summary:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [patientData]);

  // Helper function to map frequency to structured timing
  const getTimingFromFrequency = (frequency) => {
    const mapping = {
      "Once daily": { frequency: 1, period: 1, periodUnit: "d" },
      "Twice daily": { frequency: 2, period: 1, periodUnit: "d" },
      "Thrice daily": { frequency: 3, period: 1, periodUnit: "d" },
      "Four times daily": { frequency: 4, period: 1, periodUnit: "d" },
      "Every 4 hours": { frequency: 1, period: 4, periodUnit: "h" },
      "Every 6 hours": { frequency: 1, period: 6, periodUnit: "h" },
      "Every 8 hours": { frequency: 1, period: 8, periodUnit: "h" },
      "Every 12 hours": { frequency: 1, period: 12, periodUnit: "h" },
      "Once a week": { frequency: 1, period: 1, periodUnit: "wk" },
      "Alternate days": { frequency: 1, period: 2, periodUnit: "d" },
      "At bedtime": { frequency: 1, period: 1, periodUnit: "d" },
      "As needed (SOS)": { frequency: 1, period: 1, periodUnit: "d" },
      "As needed": { frequency: 1, period: 1, periodUnit: "d" }, // Legacy support
    };
    return mapping[frequency] || null;
  };

  // Function to validate medication data
  const validateMedicationData = (medications) => {
    const errors = [];
    medications.forEach((med, index) => {
      if (med.medicationDetails) {
        const medDetails = med.medicationDetails;
        const medName = `"${medDetails.name || "Unnamed medication"}" (Row ${index + 1})`;

        if (!medDetails.frequency || medDetails.frequency.trim() === "") {
          errors.push(`${medName}: Frequency field cannot be empty`);
        }
        if (!medDetails.instructions || medDetails.instructions.trim() === "") {
          errors.push(`${medName}: Instructions field cannot be empty`);
        }
        if (!medDetails.name || medDetails.name.trim() === "") {
          errors.push(`Medication Row ${index + 1}: Medicine name is required`);
        }
        if (!medDetails.dosage || medDetails.dosage.trim() === "") {
          errors.push(`${medName}: Dosage field cannot be empty`);
        }
      }
    });
    return errors;
  };

  // Print official clinical discharge summary
  const handlePrintDischargeSummary = () => {
    if (!patientData) {
      showWarning("No patient data available to print.");
      return;
    }
    const formEl = document.querySelector("#DischargeSummaryForm");
    const getValue = (dataField) => {
      if (!formEl) return "";
      const el = formEl.querySelector(`[data-field="${dataField}"]`);
      if (!el) return "";
      if (el.tagName === "SELECT") {
        const opt = el.options[el.selectedIndex];
        return opt && (opt.value || opt.text) ? opt.value || opt.text : "";
      }
      return (el.value && el.value.trim()) || "";
    };

    const dischargeData = {
      admissionDate: patientData.admissionDate || patientData.admission_date || new Date(),
      dischargeDate: patientData.dischargeDate || patientData.discharge_date || new Date(),
      ward: patientData.ward || "",
      bed: patientData.bed || "",
      diagnosis: getValue("diagnosis"),
      admissionNotes: getValue("admission"),
      clinicalSummary: getValue("clinical_course"),
      investigationsResults: getValue("investigations"),
      treatmentGiven: getValue("treatment"),
      surgicalProcedures: getValue("surgical_procedures"),
      surgicalNote: getValue("surgical_notes"),
      conditionAtDischarge: getValue("condition_at_discharge"),
      followUpInstructions: getValue("discharge"),
      doctorSignature: getValue("doctor_signature") || user?.name || "Consultant Physician",
      dischargeMedications: dischargeMeds.filter((m) => m.medicine && m.medicine.trim() !== ""),
    };

    const html = buildDischargeSummaryHtml({
      hospitalName: resolveHospitalName(user?.hospital_name),
      patient: patientData,
      discharge: dischargeData,
    });

    printDocumentHtml({
      title: `Discharge Summary - ${patientData.fullName || patientData.name || "Patient"}`,
      contentHtml: html,
    });
  };

  // Function to sign and confirm discharge summary
  const signAndConfirmDischarge = async (e) => {
    if (e) e.preventDefault();
    if (!canWriteDischarge) {
      showError("You do not have permission to modify or sign discharge summaries.");
      return;
    }
    if (!patientData) {
      showWarning("No patient data available. Please select a patient first.");
      return;
    }

    setIsLoading(true);
    try {
      const formEl = document.querySelector("#DischargeSummaryForm");
      if (!formEl) {
        showError("Form not found.");
        return;
      }

      const getValue = (dataField) => {
        const el = formEl.querySelector(`[data-field="${dataField}"]`);
        if (!el) return "";
        if (el.tagName === "SELECT") {
          const opt = el.options[el.selectedIndex];
          return opt && (opt.value || opt.text) ? opt.value || opt.text : "";
        }
        return (el.value && el.value.trim()) || "";
      };

      const dischargeMedications = dischargeMeds
        .filter((med) => med.medicine.trim() !== "" && med.dosage.trim() !== "")
        .map((med) => ({
          medicine: sanitizeText(med.medicine),
          form: med.form || undefined,
          dosage: sanitizeText(med.dosage),
          frequency: med.frequency || undefined,
          timing: getTimingFromFrequency(med.frequency) || undefined,
          duration: med.duration ? sanitizeText(med.duration) : undefined,
          durationUnit: med.durationUnit || undefined,
          instructions: med.instructions ? sanitizeText(med.instructions) : undefined,
          ...(med.snomedCode
            ? { snomedCode: med.snomedCode, snomedDisplay: med.snomedDisplay }
            : {}),
        }));

      const medicationDataForValidation = dischargeMedications.map((m) => ({
        fieldType: "medication",
        medicationDetails: {
          name: m.medicine,
          dosage: m.dosage,
          frequency: m.frequency || "",
          duration: m.duration || "",
          instructions: m.instructions || "",
        },
      }));
      const validationErrors = validateMedicationData(
        medicationDataForValidation,
      );
      if (validationErrors.length > 0) {
        showError(
          "Please complete the following medication fields before submitting:\n\n" +
            validationErrors.join("\n") +
            "\n\nTip: If you don't want to include a medication, please clear all fields in that row.",
        );
        return;
      }

      const visitId = patientData?.visitId || patientData?.visits?.[0]?.visitId;
      const patientId = patientData?._id || patientData?.id;
      if (!visitId) {
        showError("Visit ID is required to save discharge summary.");
        return;
      }

      const cleanField = (dataField) => {
        const val = getValue(dataField);
        return val ? sanitizeClinicalNote(val) : undefined;
      };

      const body = {
        admissionDate:
          patientData?.admissionDate ||
          patientData?.admission_date ||
          undefined,
        dischargeDate:
          patientData?.dischargeDate ||
          patientData?.discharge_date ||
          undefined,
        ward: patientData?.ward ? sanitizeText(patientData.ward) : undefined,
        bed: patientData?.bed ? sanitizeText(patientData.bed) : undefined,
        diagnosis: cleanField("diagnosis"),
        conditionAtDischarge: cleanField("condition_at_discharge"),
        clinicalSummary: cleanField("clinical_course"),
        admissionNotes: cleanField("admission"),
        treatmentGiven: cleanField("treatment"),
        investigationsResults: cleanField("investigations"),
        followUpInstructions: cleanField("discharge"),
        surgicalProcedures: cleanField("surgical_procedures"),
        surgicalNote: cleanField("surgical_notes"),
        doctorSignature: cleanField("doctor_signature"),
        dischargeMedications:
          dischargeMedications.length > 0 ? dischargeMedications : undefined,
      };
      const result = await saveClinicalDischargeSummary(
        visitId,
        body,
        patientId || undefined,
      );

      if (result && result.success !== false) {
        showSuccess(result.message || "Discharge summary saved successfully.");
      } else {
        showError(result?.message || "Failed to save discharge summary.");
      }
    } catch (error) {
      console.error("Error signing discharge summary:", error);
      showError(`Failed to sign discharge summary: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!patientData) {
    return (
      <div className="container-fluid">
        <ToastContainer toasts={toasts} removeToast={removeToast} />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading patient data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="container-fluid">
        <div className="opd-page">
          <div className="opd-filter">
            <div className="opd-filter-left">
              <h3>Discharge Summary</h3>
              <button
                className="btn btn-sm btn-outline-secondary ms-3"
                onClick={() => navigate("/discharge-summary")}
              >
                ← Back to Patient List
              </button>
            </div>
          </div>

          <div className="allMainFeilds">
            {!canWriteDischarge && (
              <>
                <style>{`
                  #DischargeSummaryForm input, #DischargeSummaryForm select, #DischargeSummaryForm textarea {
                    background-color: #f8f9fa !important;
                    cursor: not-allowed !important;
                    color: #495057 !important;
                  }
                `}</style>
                <div
                  className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2"
                  role="alert"
                  style={{
                    borderRadius: "8px",
                    fontSize: "13px",
                    backgroundColor: "#EBF5FF",
                    color: "#1E429F",
                    border: "1px solid #B4C6FC",
                  }}
                >
                  <i className="material-icons" style={{ fontSize: "18px" }}>visibility</i>
                  <span><strong>Read-Only Mode:</strong> You do not have permission to modify or sign discharge summaries. Form details are locked in view-only mode.</span>
                </div>
              </>
            )}
            <form id="DischargeSummaryForm" onSubmit={signAndConfirmDischarge}>
              <div className="discharge-summary-container">
                <fieldset
                  disabled={!canWriteDischarge}
                  style={{
                    border: "none",
                    padding: 0,
                    margin: 0,
                    pointerEvents: !canWriteDischarge ? "none" : "auto",
                    opacity: !canWriteDischarge ? 0.88 : 1,
                  }}
                >
                {/* Patient Header Section */}
                <div className="patient-header-section">
                  <div className="row align-items-center">
                    <div className="col-md-2">
                      <div className="patient-photo">
                        <img
                          src={
                            patientData?.photo ||
                            "https://via.placeholder.com/80x80?text=Photo"
                          }
                          alt="Patient"
                          className="img-fluid rounded"
                          style={{
                            width: "80px",
                            height: "80px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="patient-info">
                        <h3 className="patient-name mb-1">
                          {patientData?.fullName || "Patient"}, (
                          {patientData?.calculatedAge ||
                            patientData?.age ||
                            "0"}
                          Y)
                        </h3>
                        <p className="patient-location mb-1 text-muted">
                          DOB:{" "}
                          {patientData?.dob
                            ? patientData.dob.split("T")[0]
                            : patientData?.dateOfBirth
                              ? patientData.dateOfBirth.split("T")[0]
                              : "N/A"}
                        </p>
                        <p className="patient-location mb-1 text-muted">
                          {patientData?.address || "N/A"}, IN. M:{" "}
                          {patientData?.mobile || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="admission-discharge-info">
                        <div className="row">
                          <div className="col-6">
                            <div className="info-item">
                              <small className="text-muted d-flex align-items-center gap-1">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                  <line x1="16" y1="2" x2="16" y2="6"></line>
                                  <line x1="8" y1="2" x2="8" y2="6"></line>
                                  <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                                <span>
                                  Admission:{" "}
                                  {new Date().toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </span>
                              </small>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="info-item">
                              <small className="text-muted d-flex align-items-center gap-1">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                  <line x1="16" y1="2" x2="16" y2="6"></line>
                                  <line x1="8" y1="2" x2="8" y2="6"></line>
                                  <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                                <span>
                                  Discharge:{" "}
                                  {new Date().toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </span>
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Patient Details Row */}
                <div className="patient-details-row">
                  <div className="row">
                    <div className="col-md-2">
                      <div className="detail-item">
                        <small className="text-muted">Patient ID:</small>
                        <div className="detail-value">
                          {patientData?._id?.slice(-8).toUpperCase() || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="detail-item">
                        <small className="text-muted">UHID:</small>
                        <div className="detail-value">
                          {patientData?.uhid || patientData?.UHID || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="detail-item">
                        <small className="text-muted">ABHA:</small>
                        <div className="detail-value">
                          {patientData?.maskedAbha ||
                            (patientData?.ABHANumber
                              ? `**** ${patientData.ABHANumber.slice(-4)}`
                              : patientData?.isAbhaLinked || patientData?.isabhalinked
                              ? "Linked"
                              : "N/A")}
                        </div>
                        {(patientData?.ABHAAddress ||
                          patientData?.abhaaddress) && (
                          <div
                            className="detail-value"
                            style={{ fontSize: "0.8em", color: "#666" }}
                          >
                            {patientData.ABHAAddress || patientData.abhaaddress}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="detail-item">
                        <small className="text-muted">Ward:</small>
                        <div className="detail-value">
                          {patientData?.ward || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="detail-item">
                        <small className="text-muted">Bed:</small>
                        <div className="detail-value">
                          {patientData?.bed || "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Discharge Summary Form */}
                <div className="discharge-form-section">
                  <div className="row">
                    {/* Left Column */}
                    <div className="col-md-6">
                      {/* Diagnosis Section */}
                      <div className="form-section">
                        <label className="section-label">
                          Diagnosis (Final & Provisional)
                        </label>
                        <textarea
                          className="form-control discharge-textarea"
                          rows="3"
                          placeholder="Enter diagnosis details..."
                          data-field="diagnosis"
                        ></textarea>
                      </div>

                      {/* Admission Notes */}
                      <div className="form-section">
                        <label className="section-label">Admission Notes</label>
                        <textarea
                          className="form-control discharge-textarea"
                          rows="4"
                          placeholder="Enter admission notes..."
                          data-field="admission"
                        ></textarea>
                      </div>

                      {/* Investigations & Results */}
                      <div className="form-section">
                        <label className="section-label">
                          Investigations & Results
                        </label>
                        <textarea
                          className="form-control discharge-textarea"
                          rows="4"
                          placeholder="Enter investigation details and results..."
                          data-field="investigations"
                        ></textarea>
                      </div>

                      {/* Surgical Procedures */}
                      <div className="form-section">
                        <div className="row">
                          <div className="col-md-8">
                            <label className="section-label">
                              Surgical Procedures (if any)
                            </label>
                            <select
                              className="form-control"
                              data-field="surgical_procedures"
                            >
                              <option value="">Select procedure...</option>
                              <option value="appendectomy">Appendectomy</option>
                              <option value="cholecystectomy">
                                Cholecystectomy
                              </option>
                              <option value="hernia-repair">
                                Hernia Repair
                              </option>
                            </select>
                          </div>
                          <div className="col-md-4">
                            <label className="section-label">Note</label>
                            <textarea
                              className="form-control discharge-textarea"
                              rows="2"
                              placeholder="Additional notes..."
                              data-field="surgical_notes"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="col-md-6">
                      {/* Condition at Discharge */}
                      <div className="form-section">
                        <label className="section-label">
                          Condition at Discharge
                        </label>
                        <select
                          className="form-control"
                          data-field="condition_at_discharge"
                        >
                          <option value="">Select condition...</option>
                          <option value="stable">Stable</option>
                          <option value="improved">Improved</option>
                          <option value="critical">Critical</option>
                          <option value="recovered">Recovered</option>
                        </select>
                      </div>

                      {/* Clinical Summary */}
                      <div className="form-section">
                        <label className="section-label">
                          Clinical Summary
                        </label>
                        <textarea
                          className="form-control discharge-textarea"
                          rows="4"
                          placeholder="Enter clinical summary..."
                          data-field="clinical_course"
                        ></textarea>
                      </div>

                      {/* Treatment Given */}
                      <div className="form-section">
                        <label className="section-label">Treatment Given</label>
                        <textarea
                          className="form-control discharge-textarea"
                          rows="4"
                          placeholder="Enter treatment details..."
                          data-field="treatment"
                        ></textarea>
                      </div>

                      {/* Follow-up Instructions */}
                      <div className="form-section">
                        <label className="section-label">
                          Follow-up Instructions
                        </label>
                        <textarea
                          className="form-control discharge-textarea"
                          rows="4"
                          placeholder="Enter follow-up instructions..."
                          data-field="discharge"
                        ></textarea>
                      </div>

                      {/* Doctor's Signature */}
                      <div className="form-section">
                        <label className="section-label">
                          Doctor's Signature
                        </label>
                        <select
                          className="form-control"
                          data-field="doctor_signature"
                        >
                          <option value="">Select Doctor</option>
                          <option value="dr-sharma">Dr. Arav Sharma</option>
                          <option value="dr-patel">Dr. Priya Patel</option>
                          <option value="dr-kumar">Dr. Raj Kumar</option>
                        </select>
                      </div>
                    </div>

                    {/* Discharge Medications */}
                    <div className="form-section">
                      <label className="section-label">
                        Discharge Medications
                      </label>
                      <div className="text-muted mb-2">
                        <small>
                          * Medicine name and dosage are required. Search and
                          select from SNOMED CT for standardized coding.
                        </small>
                      </div>
                      <div className="medications-table">
                        <div className="row medication-header">
                          <div className="col-2">
                            <small className="text-muted">MEDICINE *</small>
                          </div>
                          <div className="col-2">
                            <small className="text-muted">FORM</small>
                          </div>
                          <div className="col-2">
                            <small className="text-muted">DOSAGE *</small>
                          </div>
                          <div className="col-2">
                            <small className="text-muted">FREQUENCY</small>
                          </div>
                          <div className="col-2">
                            <small className="text-muted">DURATION</small>
                          </div>
                          <div className="col-2">
                            <small className="text-muted">INSTRUCTIONS</small>
                          </div>
                        </div>
                        {dischargeMeds.map((medication) => (
                          <div
                            key={medication.id}
                            className="row medication-row mb-2"
                          >
                            <div
                              className="col-2"
                              style={{ position: "relative" }}
                            >
                              <MedicineAutocomplete
                                disabled={!canWriteDischarge}
                                drugName={medication.medicine}
                                snomedCode={medication.snomedCode}
                                snomedDisplay={medication.snomedDisplay}
                                compact
                                onChange={(val) => {
                                  setDischargeMeds((prev) =>
                                    prev.map((med) =>
                                      med.id === medication.id
                                        ? { ...med, medicine: val }
                                        : med,
                                    ),
                                  );
                                }}
                                onSelect={(display, code, snomedDisp) => {
                                  setDischargeMeds((prev) =>
                                    prev.map((med) =>
                                      med.id === medication.id
                                        ? {
                                            ...med,
                                            medicine: display,
                                            snomedCode: code,
                                            snomedDisplay: snomedDisp,
                                          }
                                        : med,
                                    ),
                                  );
                                }}
                                onClear={() => {
                                  setDischargeMeds((prev) =>
                                    prev.map((med) =>
                                      med.id === medication.id
                                        ? {
                                            ...med,
                                            medicine: "",
                                            snomedCode: "",
                                            snomedDisplay: "",
                                          }
                                        : med,
                                    ),
                                  );
                                }}
                                placeholder="Search medicine..."
                              />
                            </div>
                            <div className="col-2">
                              <select
                                className="form-control form-control-sm"
                                value={medication.form}
                                onChange={(e) =>
                                  updateDischargeMed(
                                    medication.id,
                                    "form",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="Tablet">Tablet</option>
                                <option value="Capsule">Capsule</option>
                                <option value="Syrup">Syrup</option>
                                <option value="Injection">Injection</option>
                                <option value="Cream">Cream/Ointment</option>
                              </select>
                            </div>
                            <div className="col-2">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Dosage"
                                value={medication.dosage}
                                onChange={(e) =>
                                  updateDischargeMed(
                                    medication.id,
                                    "dosage",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="col-2">
                              <select
                                className="form-control form-control-sm"
                                value={medication.frequency}
                                onChange={(e) =>
                                  updateDischargeMed(
                                    medication.id,
                                    "frequency",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select...</option>
                                <option value="Once daily">Once daily</option>
                                <option value="Twice daily">Twice daily</option>
                                <option value="Thrice daily">
                                  Thrice daily
                                </option>
                                <option value="Four times daily">
                                  Four times daily
                                </option>
                                <option value="Every 4 hours">
                                  Every 4 hours
                                </option>
                                <option value="Every 6 hours">
                                  Every 6 hours
                                </option>
                                <option value="Every 8 hours">
                                  Every 8 hours
                                </option>
                                <option value="Every 12 hours">
                                  Every 12 hours
                                </option>
                                <option value="Once a week">Once a week</option>
                                <option value="Alternate days">
                                  Alternate days
                                </option>
                                <option value="At bedtime">At bedtime</option>
                                <option value="As needed (SOS)">
                                  As needed (SOS)
                                </option>
                              </select>
                            </div>
                            <div className="col-2 d-flex px-1">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g., 7"
                                style={{ width: "40%" }}
                                value={medication.duration}
                                onChange={(e) =>
                                  updateDischargeMed(
                                    medication.id,
                                    "duration",
                                    e.target.value,
                                  )
                                }
                              />
                              <select
                                className="form-control form-control-sm ms-1"
                                style={{ width: "60%" }}
                                value={medication.durationUnit}
                                onChange={(e) =>
                                  updateDischargeMed(
                                    medication.id,
                                    "durationUnit",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="days">Days</option>
                                <option value="weeks">Weeks</option>
                                <option value="months">Months</option>
                              </select>
                            </div>
                            <div className="col-2">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Instructions"
                                value={medication.instructions}
                                onChange={(e) =>
                                  updateDischargeMed(
                                    medication.id,
                                    "instructions",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          </div>
                        ))}
                        {canWriteDischarge && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mt-2"
                            onClick={() => {
                              setDischargeMeds((prev) => [
                                ...prev,
                                createEmptyMed(nextMedId),
                              ]);
                              setNextMedId((prev) => prev + 1);
                            }}
                          >
                            + Add Row
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </fieldset>

                {/* Action Buttons */}
                <div className="discharge-actions">
                  <div className="row">
                    <div className="col-md-12 text-center">
                      <button
                        type="button"
                        className="customeBtn btnEmpty me-2"
                        onClick={() => navigate("/discharge-summary")}
                      >
                        {canWriteDischarge ? "Cancel" : "Back to Patient List"}
                      </button>
                      <button
                        type="button"
                        className="customeBtn btnLightBlueBorder me-2 d-inline-flex align-items-center gap-2"
                        onClick={handlePrintDischargeSummary}
                        title="Print Official Inpatient Discharge Summary"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 6 2 18 2 18 9"></polyline>
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                          <rect x="6" y="14" width="12" height="8"></rect>
                        </svg>
                        <span>Print Discharge Summary</span>
                      </button>
                      {!canWriteDischarge ? (
                        <span className="badge bg-secondary px-3 py-2 ms-2" style={{ fontSize: "13px" }}>
                          Read-Only Mode ({user?.role_name || "Viewing"})
                        </span>
                      ) : (
                        <button
                          className="customeBtn btnLightBlue"
                          type="submit"
                          disabled={isLoading}
                          style={{
                            opacity: isLoading ? 0.6 : 1,
                            cursor: isLoading ? "not-allowed" : "pointer",
                          }}
                        >
                          {isLoading ? "Signing..." : "Sign & Confirm"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default DischargeSummaryForm;
